<template>
  <div
    class="group"
    :class="{
      'flex items-center justify-between': flex,
      'w-full': wFull,
      '!gap-2': gap,
    }"
  >
    <t-label
      v-if="label && !labelIn && !$slots.label"
      :for="uuid"
      :disabled="disabled"
      :changed="changed"
      :tooltip="resetTooltip"
      :reserve-reset="flex"
      @reset="$emit('reset')"
      >{{ label }}</t-label
    >
    <t-label
      v-if="$slots.label && !labelIn"
      v-tooltip="{
        content: label,
      }"
      :for="uuid"
      :disabled="disabled"
      :changed="changed"
      :tooltip="resetTooltip"
      :reserve-reset="flex"
      @reset="$emit('reset')"
    >
      <slot name="label"></slot>
    </t-label>
    <div
      ref="outsideNumberInput"
      class="inline-flex overflow-hidden rounded-md transition duration-300 focus-within:outline focus-within:outline-2 focus-within:outline-accent-1"
      :class="{
        'w-full': wFull,
        '!outline !outline-2 !outline-destructive-1': showInvalidError,
      }"
    >
      <div
        class="flex rounded-md border-x border-y border-solid border-transparent px-2.5 font-inter text-xs font-medium leading-[30px] transition duration-300"
        :class="{
          'bg-gray-600 text-gray-50 group-focus-within:bg-gray-700 group-focus-within:text-gray-10 group-hover:border-gray-400':
            !inverse,
          'bg-[color-mix(in_srgb,var(--c-g100)_40%,var(--c-g10))] text-gray-400 group-focus-within:bg-[color-mix(in_srgb,var(--c-g100)_15%,var(--c-g10))] group-hover:border-[color-mix(in_srgb,var(--c-g100)_70%,var(--c-g10))]':
            inverse,
          '!pl-2 !pr-1': controls,
          '!border-transparent !bg-gray-600 !text-gray-200': disabled && !inverse,
          '!border-transparent !bg-gray-50 !brightness-110 !text-gray-300': disabled && inverse,
          'w-full justify-between': wFull,
        }"
        @click="($refs.thisInput as HTMLInputElement).focus()"
      >
        <div v-if="label && labelIn" class="text-[9px] font-medium text-gray-200">
          <span>{{ label }}</span>
        </div>
        <input
          :id="uuid"
          ref="thisInput"
          :value="inputValue"
          :pattern="regexPattern"
          type="number"
          :min="min"
          :max="max"
          :step="step"
          class="border-none p-0 text-center font-inter text-xs font-medium leading-[30px] outline-none transition duration-300 disabled:!cursor-not-allowed"
          :class="{
            'bg-gray-600 text-gray-50 disabled:bg-gray-600 disabled:text-gray-200 group-focus-within:bg-gray-700 group-focus-within:text-gray-10':
              !inverse,
            'bg-[color-mix(in_srgb,var(--c-g100)_40%,var(--c-g10))] text-gray-400 group-focus-within:bg-[color-mix(in_srgb,var(--c-g100)_15%,var(--c-g10))] disabled:text-gray-200':
              inverse,
          }"
          :style="{
            width: width + 'px',
          }"
          :disabled="disabled"
          @input="handleInput"
          @blur="onBlur"
          @wheel="onWheel"
        />
        <div v-if="unit !== ''">
          <span>{{ unit }}</span>
        </div>
        <div v-if="controls" class="my-[2px] ml-2">
          <button
            class="block cursor-pointer border-none py-[2.5px] transition duration-300 disabled:!cursor-not-allowed disabled:!border-transparent"
            :class="{
              'bg-gray-600 text-gray-100 hover:bg-gray-400 hover:text-gray-50 disabled:bg-gray-600 disabled:text-gray-300 group-hover:border-gray-400':
                !inverse,
              'bg-[color-mix(in_srgb,var(--c-g100)_40%,var(--c-g10))] text-gray-600 hover:bg-[color-mix(in_srgb,var(--c-g100)_70%,var(--c-g10))] disabled:text-gray-300':
                inverse,
            }"
            :disabled="disabled"
            data-testid="increment"
            @click="(e) => (e.shiftKey ? add(5) : add(Number(step)))"
          >
            <svg
              width="12"
              height="8"
              viewBox="0 0 12 9"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5.22052 2.72852L2.22052 5.72852C2.00958 5.93945 1.93927 6.26758 2.05645 6.54883C2.17364 6.83008 2.45489 7.01758 2.75958 7.01758H8.75958C9.06427 7.01758 9.32208 6.83008 9.43927 6.54883C9.55645 6.26758 9.48614 5.93945 9.2752 5.72852L6.2752 2.72852C5.99395 2.42383 5.50177 2.42383 5.22052 2.72852Z"
                fill="currentColor"
              />
            </svg>
          </button>
          <button
            class="block cursor-pointer border-none py-[2.5px] transition duration-300 disabled:!cursor-not-allowed disabled:!border-transparent"
            :class="{
              'bg-gray-600 text-gray-100 hover:bg-gray-400 hover:text-gray-50 disabled:bg-gray-600 disabled:text-gray-300 group-hover:border-gray-400':
                !inverse,
              'bg-[color-mix(in_srgb,var(--c-g100)_40%,var(--c-g10))] text-gray-600 hover:bg-[color-mix(in_srgb,var(--c-g100)_70%,var(--c-g10))] disabled:text-gray-300':
                inverse,
            }"
            :disabled="disabled"
            data-testid="decrement"
            @click="(e) => (e.shiftKey ? substract(5) : substract(Number(step)))"
          >
            <svg
              width="12"
              height="8"
              viewBox="0 0 12 9"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5.22052 6.78906L2.22052 3.78906C2.00958 3.57812 1.93927 3.25 2.05645 2.96875C2.17364 2.6875 2.45489 2.5 2.75958 2.5H8.75958C9.06427 2.5 9.32208 2.6875 9.43927 2.96875C9.55645 3.25 9.48614 3.57812 9.2752 3.78906L6.2752 6.78906C5.99395 7.09375 5.50177 7.09375 5.22052 6.78906Z"
                fill="currentColor"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { v4 as uuidv4 } from "uuid";
import TLabel from "../TLabel/TLabel.vue";
import { onClickOutside } from "@vueuse/core";
import { ref, watch, onBeforeMount, onBeforeUnmount, useTemplateRef } from "vue";

const props = withDefaults(
  defineProps<{
    controls?: boolean;
    min?: number;
    max?: number;
    disabled?: boolean;
    alt?: boolean;
    label?: string;
    unit?: string;
    noBorderLeft?: boolean;
    regexPattern?: string;
    step?: string;
    width?: number;
    flex?: boolean;
    labelIn?: boolean;
    wFull?: boolean;
    changed?: boolean;
    resetTooltip?: string;
    inverse?: boolean;
    gap?: boolean;
  }>(),
  {
    controls: true,
    min: 0,
    unit: "px",
    noBorderLeft: true,
    step: "1",
    width: 44,
    resetTooltip: "Clear value",
  },
);
defineEmits(["reset"]);

const modelValue = defineModel({
  type: Number,
  default: 0,
});

const uuid = uuidv4();
const active = ref(false);
const outsideNumberInput = ref();
const thisInput = useTemplateRef("thisInput");
const inputValue = ref<number | string>(modelValue.value);
const showInvalidError = ref(false);

watch(modelValue, (newVal) => {
  inputValue.value = newVal;
  showInvalidError.value = false;
});

function handleInput(e: Event) {
  const val = (e.target as HTMLInputElement).value;
  inputValue.value = val;
  if (val === "") {
    // Allow clearing the input without marking it invalid or updating the model.
    showInvalidError.value = false;
    return;
  }
  if (isNaN(Number(val))) {
    showInvalidError.value = true;
    return;
  }
  const numVal = Number(val);
  if (numVal === 0 && /^0+$/.test(val) && val.length > 1) {
    if (props.min !== undefined && props.min > 0) {
      showInvalidError.value = true;
    } else {
      showInvalidError.value = false;
    }
    return;
  }
  inputValue.value = numVal;
  if (
    (props.min !== undefined && numVal < props.min) ||
    (props.max !== undefined && numVal > props.max)
  ) {
    showInvalidError.value = true;
    return;
  }

  showInvalidError.value = false;
  modelValue.value = numVal;
}

function checkValue(value: string) {
  let val = Number(value);
  if (value === "") {
    val = modelValue.value;
  }

  if (props.min !== undefined) {
    if (val < props.min) {
      inputValue.value = props.min;
      showInvalidError.value = false;
      modelValue.value = props.min;
      return;
    }
  }

  if (props.max !== undefined) {
    if (val > props.max) {
      inputValue.value = props.max;
      showInvalidError.value = false;
      modelValue.value = props.max;
      return;
    }
  }
  inputValue.value = val;
}

function onBlur(val: any) {
  if (val.target.value !== undefined) {
    const roundedValue = Math.round(Number(val.target.value) * 100) / 100;
    checkValue(roundedValue.toString());
  }
}

function onWheel(event: WheelEvent) {
  if (event.altKey) {
    event.preventDefault();

    if (event.deltaY < 0) {
      add();
    } else {
      substract();
    }
  }
}

onClickOutside(outsideNumberInput, () => {
  active.value = false;
});

function add(toAdd = 1) {
  if (!props.disabled && !isNaN(Number(inputValue.value))) {
    active.value = true;

    const newValue = Number(inputValue.value) + toAdd;

    if (props.max && newValue > props.max) {
      modelValue.value = Math.round(Number(props.max) * 100) / 100;
    } else {
      modelValue.value = Math.round(Number(newValue) * 100) / 100;
    }
  }
}

function substract(toSub = 1) {
  if (!props.disabled && !isNaN(Number(inputValue.value))) {
    active.value = true;

    const newValue = Number(inputValue.value) - toSub;

    if (newValue < props.min) {
      modelValue.value = Math.round(Number(props.min) * 100) / 100;
    } else {
      modelValue.value = Math.round(Number(newValue) * 100) / 100;
    }
  }
}

function focusChanged(event: Event) {
  const el = event.target;

  if (!el) {
    return;
  }

  //@ts-expect-error
  if (el.id === uuid) {
    active.value = true;
  } else {
    active.value = false;
  }
}

onBeforeMount(() => {
  document.addEventListener("focusin", focusChanged);
});

onBeforeUnmount(() => {
  document.removeEventListener("focusin", focusChanged);
  if (thisInput.value) {
    checkValue((thisInput.value as HTMLInputElement).value);
  }
});
</script>
<style lang="scss">
// @import "./TInputNumber.scss";
input[type="number"] {
  -moz-appearance: textfield;
  /* Firefox */
}

input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  /* display: none; <- Crashes Chrome on hover */
  -webkit-appearance: none;
  margin: 0;
  /* <-- Apparently some margin are still there even though it's hidden */
}
</style>
